import React, { useEffect, useMemo, useState } from "react";
import { get, post } from "../../services/api";
import { MaterialReactTable } from "material-react-table";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import CommunicationModal from "./CommunicationModal";
import StudentProfile from "./student_profile/StudentProfile";
import CloseIcon from "@mui/icons-material/Close";

function StudentListings() {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");

  const navigate = useNavigate();

  const [columnLabels, setColumnLabels] = useState({});
  const [studentsData, setStudentsData] = useState([]);
  const [tablePreferences, setTablePreferences] = useState(null);
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});
  const [filterModes, setFilterModes] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [refetchTrigger, setRefetchTrigger] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const fetchStudents = async () => {
    try {
      const response = await get(
        `Student/GetAllStudentByCenterIdAsync?centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setStudentsData(response?.data);
        setColumnLabels(
          Object.fromEntries(
            response?.columns.map((col) => [col.key, col.label])
          )
        );
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  const fetchTablePreferences = async () => {
    try {
      const response = await get(
        `CommonSettings/GetDatatablePreferences?userId=${userId}&TableName=studentListings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const defaultFilterModes = Object.fromEntries(
        response.columnOrder.map((key) => [
          key,
          key.includes("Id") || key.includes("No") || key.startsWith("is")
            ? "equals"
            : key.includes("Date")
            ? "between"
            : "contains",
        ])
      );
      const preferences = {
        columnOrder: response.columnOrder || [],
        columnVisibility: response.columnVisibility || {},
        columnSortability: response.columnSortability || {},
        columnSorting: response.columnSorting || [],
        columnFilterability: response.columnFilterability || {},
        columnWidths: Object.fromEntries(
          Object.entries(response.columnWidths || {}).map(([key, value]) => [
            key,
            value || 150,
          ])
        ),
        columnFilterModes: response.columnFilterModes || defaultFilterModes,
      };
      setTablePreferences(preferences);
      setColumnOrder(preferences.columnOrder);
      setColumnVisibility(preferences.columnVisibility);
      setSorting(preferences.columnSorting.slice(0, 1));
      setColumnSizing(preferences.columnWidths);
      setFilterModes(preferences.columnFilterModes);
    } catch (error) {
      console.error("Error fetching table preferences:", error);
      toast.error("Failed to fetch table preferences");
    }
  };

  const saveTablePreferences = async () => {
    try {
      const filteredColumnSizing = Object.fromEntries(
        Object.entries(columnSizing)
          .filter(([key]) => key !== "mrt-row-spacer" && key !== "actions")
          .map(([key, value]) => [key, Math.max(value, 50)])
      );
      const preferences = {
        columnOrder: columnOrder.filter(
          (key) => key !== "mrt-row-spacer" && key !== "actions"
        ),
        columnVisibility: Object.fromEntries(
          Object.entries(columnVisibility).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
        columnSortability: Object.fromEntries(
          Object.entries(tablePreferences.columnSortability).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
        columnSorting: sorting
          .filter((s) => s.id !== "actions")
          .map((s) => ({
            id: s.id,
            desc: s.desc === "True" || s.desc === true,
          })),
        columnFilterability: Object.fromEntries(
          Object.entries(tablePreferences.columnFilterability).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
        columnWidths: filteredColumnSizing,
        columnFilterModes: Object.fromEntries(
          Object.entries(filterModes).filter(
            ([key]) => key !== "mrt-row-spacer" && key !== "actions"
          )
        ),
      };
      const payload = {
        userId,
        tableId: "studentListings",
        preferences,
      };
      const response = await post(
        `CommonSettings/SaveDatatableFiters`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Preferences saved successfully");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error("Error saving preferences:", error);
      toast.error(`Failed to save preferences: ${error.message}`);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([fetchStudents(), fetchTablePreferences()]);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handlePrintStudentCardClick = async (firstRegistrationId) => {
    if (!firstRegistrationId) {
      alert("Error");
      return;
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL;

    try {
      const response = await fetch(
        `${API_BASE_URL}Student/GenerateStudentCard?FirstRegistrationId=${firstRegistrationId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error("Error opening the invoice:", error);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this student?"
    );
    if (!confirmed) return;
    try {
      const response = await get(
        `Student/DeleteStudentByIdAsync?userId=${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success(response.message || "Student deleted successfully!");
        setRefetchTrigger((prev) => !prev);
        fetchData();
      } else {
        toast.error(response.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting student:", error);
      toast.error("An error occurred while deleting the student.");
    }
  };

  const handleRowClick = (row) => {
    navigate(`/student-form?studentId=${row.original.id}`);
  };

  const columns = useMemo(
    () => [
      ...columnOrder
        .filter((key) => key !== "mrt-row-spacer" && key !== "actions")
        .map((key) => ({
          accessorKey: key,
          id: key,
          header: columnLabels[key] || key,
          filterVariant: key.includes("is") ? "checkbox" : "text",
          enableColumnFilter:
            tablePreferences?.columnFilterability?.[key] ?? true,
          enableColumnFilterModes: true,
          filterModeOptions: [
            "contains",
            "startsWith",
            "endsWith",
            "equals",
            "notEquals",
            "greaterThan",
            "lessThan",
            "greaterThanOrEqualTo",
            "lessThanOrEqualTo",
            "between",
            "empty",
            "notEmpty",
          ],
          filterFn:
            filterModes[key] ||
            (key.includes("Id") || key.includes("No") || key.startsWith("is")
              ? "equals"
              : key.includes("Date")
              ? "between"
              : "contains"),
          enableSorting: tablePreferences?.columnSortability?.[key] ?? true,
          size: columnSizing[key] || 150,
          minSize: 50,
          maxSize: 1000,
        })),
      {
        accessorKey: "actions",
        id: "actions",
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
        enablePinning: true,
        pinned: "right",
        size: 150,
        Cell: ({ row }) => (
          <div className="d-flex align-items-center">
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Print Student Card"
              onClick={(e) => {
                e.stopPropagation();
                handlePrintStudentCardClick(row.original.id);
              }}
            >
              <i className="text-primary fs-8 fas fa-print"></i>
            </button>
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.original.id);
              }}
            >
              <i className="text-primary fs-8 fas fa-trash"></i>
            </button>
            {/* <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Communication"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudent(row.original);
                setIsModalOpen(true);
              }}
            >
              <i className="text-primary fs-8 fas fa-comments"></i>
            </button> */}
            <button
              type="button"
              className="btn btn-with-icon-and-tooltip"
              data-tooltip-id="global-tooltip"
              data-tooltip-content="Student Profile"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedStudent(row.original);
                setIsProfileModalOpen(true);
              }}
            >
              <i className="text-primary fs-8 fas fa-user"></i>
            </button>
          </div>
        ),
      },
    ],
    [columnOrder, columnLabels, tablePreferences, filterModes, columnSizing]
  );

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(studentsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Students");
    XLSX.writeFile(workbook, "students_data.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF("landscape", "pt", "A4");

    const columns = columnOrder
      .filter((key) => key !== "mrt-row-spacer")
      .map((key) => ({
        header: columnLabels[key] || key,
        dataKey: key,
      }));

    const rows = studentsData.map((row) => {
      const filteredRow = {};
      columns.forEach((col) => {
        filteredRow[col.dataKey] = row[col.dataKey] ?? "";
      });
      return filteredRow;
    });

    doc.setFontSize(14);
    doc.text("Students Data", 40, 40);

    doc.autoTable({
      columns,
      body: rows,
      startY: 60,
      theme: "striped",
      styles: {
        fontSize: 8,
        cellPadding: 4,
        overflow: "linebreak",
        minCellHeight: 20,
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 60 },
        2: { cellWidth: 60 },
        email: { cellWidth: 120 },
        imagePath: { cellWidth: 140 },
        studentImage: { cellWidth: 80 },
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: [255, 255, 255],
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
        valign: "middle",
      },
      margin: { top: 60, left: 40, right: 40 },
      tableWidth: "wrap",
    });

    doc.save("students_data.pdf");
  };

  if (isLoading) {
    return <i className="fas fa-spinner"></i>;
  }

  return (
    <>
      <div className="page-header d-flex mb-3">
        <h2 className="page-heading fs-7">Students</h2>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="custom-mrt-table-wrapper">
            <MaterialReactTable
              columns={columns}
              data={studentsData}
              enableColumnFilters
              enableGlobalFilter
              enableColumnDragging
              enableColumnResizing
              enableHiding
              enableColumnOrdering
              enableSorting
              enableStickyHeader
              enableColumnFilterModes
              defaultColumn={{
                minSize: 50,
                maxSize: 1000,
                size: 150,
              }}
              initialState={{
                showColumnFilters: true,
                columnOrder,
                columnVisibility,
                sorting,
                columnSizing,
                columnFilters,
                density: "compact",
                columnPinning: { right: ["actions"] },
              }}
              state={{
                columnOrder,
                columnVisibility,
                sorting,
                columnSizing,
                columnFilters,
              }}
              onColumnOrderChange={(newOrder) =>
                setColumnOrder(
                  newOrder.filter((key) => key !== "mrt-row-spacer")
                )
              }
              onColumnVisibilityChange={setColumnVisibility}
              onSortingChange={(updater) => {
                const newSorting =
                  typeof updater === "function" ? updater(sorting) : updater;
                setSorting(newSorting.slice(0, 1));
              }}
              onColumnSizingChange={(updater) => {
                const newSizing =
                  typeof updater === "function"
                    ? updater(columnSizing)
                    : updater;
                setColumnSizing(
                  Object.fromEntries(
                    Object.entries(newSizing).filter(
                      ([key]) => key !== "mrt-row-spacer"
                    )
                  )
                );
              }}
              onColumnFiltersChange={(updater) => {
                const newFilters =
                  typeof updater === "function"
                    ? updater(columnFilters)
                    : updater;
                setColumnFilters(
                  newFilters.filter((filter) => filter.id !== "mrt-row-spacer")
                );
                const newFilterModes = { ...filterModes };
                newFilters.forEach((filter) => {
                  if (filter.filterFn && filter.id !== "mrt-row-spacer") {
                    newFilterModes[filter.id] = filter.filterFn;
                  }
                });
                setFilterModes(newFilterModes);
              }}
              muiTableBodyRowProps={({ row }) => ({
                onClick: () => handleRowClick(row),
                sx: { cursor: "pointer" },
              })}
              muiTableProps={{
                className: "custom-mrt-table",
              }}
              muiTableHeadCellProps={{
                className: "custom-mrt-table-head-cell",
              }}
              muiTableBodyCellProps={{
                className: "custom-mrt-table-body-cell",
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <div className="d-flex gap-1 align-items-center">
                  <button
                    type="button"
                    className="btn btn-with-icon-and-tooltip"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Save Preferences"
                    onClick={saveTablePreferences}
                  >
                    <i className="text-primary fs-7 fas fa-save"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-with-icon-and-tooltip"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Export to Excel"
                    onClick={exportToExcel}
                  >
                    <i className="text-success fs-7 fas fa-file-excel"></i>
                  </button>
                  <button
                    type="button"
                    className="btn btn-with-icon-and-tooltip"
                    data-tooltip-id="global-tooltip"
                    data-tooltip-content="Export to PDF"
                    onClick={exportToPDF}
                  >
                    <i className="text-danger fs-7 fas fa-file-pdf"></i>
                  </button>
                </div>
              )}
            />
          </div>
        </div>
      </div>

      <CommunicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        student={selectedStudent}
      />

      {isProfileModalOpen && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            style={{ display: "block" }}
          >
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <button
                    type="button"
                    className="btn-close"
                    onClick={setIsProfileModalOpen(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <StudentProfile
                    student={selectedStudent}
                    onClose={() => setIsProfileModalOpen(false)}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* <Dialog
        open={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Student Profile
          <IconButton
            aria-label="close"
            onClick={() => setIsProfileModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <StudentProfile
            student={selectedStudent}
            onClose={() => setIsProfileModalOpen(false)}
          />
        </DialogContent>
      </Dialog> */}
    </>
  );
}

export default StudentListings;
